import React, { useState, useEffect } from 'react';
import { 
  Printer, Scan, Bluetooth, Usb, Wifi, RefreshCw, Settings, 
  Check, X, AlertCircle, Loader2, Cable, Signal, Monitor,
  Plus, Trash2, Edit2, Power, PowerOff, TestTube, ChevronRight,
  Smartphone, HardDrive, Radio
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Device type definitions
const DEVICE_TYPES = {
  BARCODE_SCANNER: { name: 'Barcode Scanner', icon: Scan, color: 'blue' },
  RECEIPT_PRINTER: { name: 'Receipt Printer', icon: Printer, color: 'green' },
  LABEL_PRINTER: { name: 'Label Printer', icon: Printer, color: 'purple' },
  SCALE: { name: 'Digital Scale', icon: Monitor, color: 'orange' },
  CARD_READER: { name: 'Card Reader', icon: Smartphone, color: 'pink' },
  UNKNOWN: { name: 'Unknown Device', icon: HardDrive, color: 'gray' }
};

// Known device identifiers for AI detection
const KNOWN_DEVICES = {
  // Barcode Scanners
  'Honeywell': { type: 'BARCODE_SCANNER', brand: 'Honeywell' },
  'Symbol': { type: 'BARCODE_SCANNER', brand: 'Symbol/Zebra' },
  'Zebra': { type: 'BARCODE_SCANNER', brand: 'Zebra' },
  'Datalogic': { type: 'BARCODE_SCANNER', brand: 'Datalogic' },
  'Socket Mobile': { type: 'BARCODE_SCANNER', brand: 'Socket Mobile' },
  'Opticon': { type: 'BARCODE_SCANNER', brand: 'Opticon' },
  'CipherLab': { type: 'BARCODE_SCANNER', brand: 'CipherLab' },
  'Motorola': { type: 'BARCODE_SCANNER', brand: 'Motorola' },
  'HID': { type: 'BARCODE_SCANNER', brand: 'HID' },
  // Receipt Printers
  'EPSON': { type: 'RECEIPT_PRINTER', brand: 'Epson' },
  'Star Micronics': { type: 'RECEIPT_PRINTER', brand: 'Star Micronics' },
  'Citizen': { type: 'RECEIPT_PRINTER', brand: 'Citizen' },
  'Bixolon': { type: 'RECEIPT_PRINTER', brand: 'Bixolon' },
  'TSP': { type: 'RECEIPT_PRINTER', brand: 'Star TSP' },
  // Label Printers
  'DYMO': { type: 'LABEL_PRINTER', brand: 'DYMO' },
  'Brother': { type: 'LABEL_PRINTER', brand: 'Brother' },
  'Rollo': { type: 'LABEL_PRINTER', brand: 'Rollo' },
  // Scales
  'CAS': { type: 'SCALE', brand: 'CAS' },
  'Fairbanks': { type: 'SCALE', brand: 'Fairbanks' },
};

const DeviceSettingsPortal = () => {
  const [devices, setDevices] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [bluetoothSupported, setBluetoothSupported] = useState(false);
  const [usbSupported, setUsbSupported] = useState(false);
  const [serialSupported, setSerialSupported] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showAddManual, setShowAddManual] = useState(false);
  const [manualDevice, setManualDevice] = useState({
    name: '',
    type: 'BARCODE_SCANNER',
    connection: 'usb',
    port: ''
  });

  // Check browser capabilities on mount
  useEffect(() => {
    setBluetoothSupported('bluetooth' in navigator);
    setUsbSupported('usb' in navigator);
    setSerialSupported('serial' in navigator);
    
    // Load saved devices from localStorage
    const savedDevices = localStorage.getItem('asap247_devices');
    if (savedDevices) {
      setDevices(JSON.parse(savedDevices));
    }
  }, []);

  // Save devices to localStorage whenever they change
  useEffect(() => {
    if (devices.length > 0) {
      localStorage.setItem('asap247_devices', JSON.stringify(devices));
    }
  }, [devices]);

  // AI-powered device identification
  const identifyDevice = (deviceInfo) => {
    const { name = '', productName = '', manufacturerName = '' } = deviceInfo;
    const searchString = `${name} ${productName} ${manufacturerName}`.toLowerCase();
    
    for (const [keyword, info] of Object.entries(KNOWN_DEVICES)) {
      if (searchString.includes(keyword.toLowerCase())) {
        return info;
      }
    }
    
    // AI heuristics for unknown devices
    if (searchString.includes('scanner') || searchString.includes('barcode') || searchString.includes('hid')) {
      return { type: 'BARCODE_SCANNER', brand: 'Unknown Scanner' };
    }
    if (searchString.includes('printer') || searchString.includes('pos') || searchString.includes('receipt')) {
      return { type: 'RECEIPT_PRINTER', brand: 'Unknown Printer' };
    }
    if (searchString.includes('label') || searchString.includes('thermal')) {
      return { type: 'LABEL_PRINTER', brand: 'Unknown Label Printer' };
    }
    if (searchString.includes('scale') || searchString.includes('weight')) {
      return { type: 'SCALE', brand: 'Unknown Scale' };
    }
    
    return { type: 'UNKNOWN', brand: 'Unknown' };
  };

  // Scan for Bluetooth devices
  const scanBluetooth = async () => {
    if (!bluetoothSupported) {
      toast.error('Bluetooth is not supported in this browser');
      return;
    }

    try {
      toast.info('Scanning for Bluetooth devices... Please select a device when prompted.');
      
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: ['battery_service', 'device_information']
      });

      const deviceId = identifyDevice({ name: device.name });
      const newDevice = {
        id: `bt_${Date.now()}`,
        name: device.name || 'Unknown Bluetooth Device',
        type: deviceId.type,
        brand: deviceId.brand,
        connection: 'bluetooth',
        status: 'connected',
        bluetoothDevice: device,
        addedAt: new Date().toISOString()
      };

      setDevices(prev => {
        const existing = prev.find(d => d.name === newDevice.name && d.connection === 'bluetooth');
        if (existing) {
          toast.info(`${newDevice.name} is already added`);
          return prev;
        }
        toast.success(`Added ${DEVICE_TYPES[deviceId.type].name}: ${newDevice.name}`);
        return [...prev, newDevice];
      });

    } catch (error) {
      if (error.name === 'NotFoundError') {
        toast.info('No device selected');
      } else {
        console.error('Bluetooth error:', error);
        toast.error('Failed to connect to Bluetooth device');
      }
    }
  };

  // Scan for USB devices
  const scanUSB = async () => {
    if (!usbSupported) {
      toast.error('WebUSB is not supported in this browser. Try Chrome or Edge.');
      return;
    }

    try {
      toast.info('Select a USB device when prompted...');
      
      const device = await navigator.usb.requestDevice({
        filters: [] // Accept all devices
      });

      const deviceId = identifyDevice({
        name: device.productName,
        productName: device.productName,
        manufacturerName: device.manufacturerName
      });

      const newDevice = {
        id: `usb_${Date.now()}`,
        name: device.productName || `USB Device (${device.vendorId}:${device.productId})`,
        type: deviceId.type,
        brand: deviceId.brand || device.manufacturerName,
        connection: 'usb',
        vendorId: device.vendorId,
        productId: device.productId,
        status: 'connected',
        usbDevice: device,
        addedAt: new Date().toISOString()
      };

      setDevices(prev => {
        const existing = prev.find(d => d.vendorId === device.vendorId && d.productId === device.productId);
        if (existing) {
          toast.info(`${newDevice.name} is already added`);
          return prev;
        }
        toast.success(`Added ${DEVICE_TYPES[deviceId.type].name}: ${newDevice.name}`);
        return [...prev, newDevice];
      });

    } catch (error) {
      if (error.name === 'NotFoundError') {
        toast.info('No device selected');
      } else {
        console.error('USB error:', error);
        toast.error('Failed to connect to USB device');
      }
    }
  };

  // Scan for Serial Port devices
  const scanSerial = async () => {
    if (!serialSupported) {
      toast.error('Web Serial is not supported in this browser. Try Chrome or Edge.');
      return;
    }

    try {
      toast.info('Select a serial port device when prompted...');
      
      const port = await navigator.serial.requestPort();
      const info = port.getInfo();

      const deviceId = identifyDevice({
        name: `Serial Device ${info.usbVendorId || 'Unknown'}`,
        manufacturerName: ''
      });

      const newDevice = {
        id: `serial_${Date.now()}`,
        name: `Serial Device (${info.usbVendorId || 'COM'}:${info.usbProductId || 'Port'})`,
        type: deviceId.type,
        brand: deviceId.brand,
        connection: 'serial',
        vendorId: info.usbVendorId,
        productId: info.usbProductId,
        status: 'connected',
        serialPort: port,
        addedAt: new Date().toISOString()
      };

      setDevices(prev => {
        toast.success(`Added ${DEVICE_TYPES[deviceId.type].name}: ${newDevice.name}`);
        return [...prev, newDevice];
      });

    } catch (error) {
      if (error.name === 'NotFoundError') {
        toast.info('No port selected');
      } else {
        console.error('Serial error:', error);
        toast.error('Failed to connect to serial device');
      }
    }
  };

  // Scan all connection types
  const scanAllDevices = async () => {
    setScanning(true);
    toast.info('Starting device scan...');

    // Sequential scanning to avoid permission conflicts
    try {
      if (usbSupported) {
        await scanUSB();
      }
    } catch (e) {}

    try {
      if (serialSupported) {
        await scanSerial();
      }
    } catch (e) {}

    try {
      if (bluetoothSupported) {
        await scanBluetooth();
      }
    } catch (e) {}

    setScanning(false);
    toast.success('Device scan complete');
  };

  // Test device connection
  const testDevice = async (device) => {
    toast.info(`Testing ${device.name}...`);
    
    // Simulate test
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    if (device.connection === 'bluetooth' && device.bluetoothDevice) {
      try {
        if (device.bluetoothDevice.gatt.connected) {
          toast.success(`${device.name} is responding correctly!`);
          return;
        }
      } catch (e) {}
    }
    
    // For demo, randomly succeed/fail
    if (Math.random() > 0.3) {
      toast.success(`${device.name} is responding correctly!`);
      setDevices(prev => prev.map(d => 
        d.id === device.id ? { ...d, status: 'connected', lastTested: new Date().toISOString() } : d
      ));
    } else {
      toast.error(`${device.name} is not responding. Check connection.`);
      setDevices(prev => prev.map(d => 
        d.id === device.id ? { ...d, status: 'disconnected' } : d
      ));
    }
  };

  // Remove device
  const removeDevice = (deviceId) => {
    setDevices(prev => prev.filter(d => d.id !== deviceId));
    toast.success('Device removed');
  };

  // Add manual device
  const addManualDevice = () => {
    if (!manualDevice.name) {
      toast.error('Please enter a device name');
      return;
    }

    const newDevice = {
      id: `manual_${Date.now()}`,
      name: manualDevice.name,
      type: manualDevice.type,
      brand: 'Manual Entry',
      connection: manualDevice.connection,
      port: manualDevice.port,
      status: 'unknown',
      addedAt: new Date().toISOString()
    };

    setDevices(prev => [...prev, newDevice]);
    setShowAddManual(false);
    setManualDevice({ name: '', type: 'BARCODE_SCANNER', connection: 'usb', port: '' });
    toast.success(`Added ${newDevice.name}`);
  };

  // Get connection icon
  const getConnectionIcon = (connection) => {
    switch (connection) {
      case 'bluetooth': return <Bluetooth className="w-4 h-4" />;
      case 'usb': return <Usb className="w-4 h-4" />;
      case 'serial': return <Cable className="w-4 h-4" />;
      case 'wifi': return <Wifi className="w-4 h-4" />;
      default: return <Cable className="w-4 h-4" />;
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'connected':
        return <Badge className="bg-green-100 text-green-700"><Check className="w-3 h-3 mr-1" />Connected</Badge>;
      case 'disconnected':
        return <Badge className="bg-red-100 text-red-700"><X className="w-3 h-3 mr-1" />Disconnected</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700"><AlertCircle className="w-3 h-3 mr-1" />Unknown</Badge>;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-2">
            <Settings className="w-7 h-7 text-blue-500" />
            Device Settings
          </h1>
          <p className="text-gray-500 mt-1">
            AI-powered detection for printers, scanners, and POS devices
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowAddManual(true)} 
            variant="outline"
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Manually
          </Button>
          <Button 
            onClick={scanAllDevices} 
            disabled={scanning}
            className="bg-blue-500 hover:bg-blue-600 flex items-center gap-2"
          >
            {scanning ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Scan for Devices
          </Button>
        </div>
      </div>

      {/* Browser Capabilities */}
      <Card className="p-4 mb-6 bg-gradient-to-r from-gray-50 to-blue-50">
        <h3 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
          <Monitor className="w-5 h-5 text-blue-500" />
          Browser Capabilities
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className={`flex items-center gap-2 p-3 rounded-lg ${bluetoothSupported ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <Bluetooth className={`w-5 h-5 ${bluetoothSupported ? 'text-green-600' : 'text-red-600'}`} />
            <div>
              <p className="font-medium text-sm">Bluetooth</p>
              <p className="text-xs text-gray-500">{bluetoothSupported ? 'Supported' : 'Not Supported'}</p>
            </div>
            {bluetoothSupported && (
              <Button size="sm" variant="ghost" onClick={scanBluetooth} className="ml-auto">
                <Signal className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          <div className={`flex items-center gap-2 p-3 rounded-lg ${usbSupported ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <Usb className={`w-5 h-5 ${usbSupported ? 'text-green-600' : 'text-red-600'}`} />
            <div>
              <p className="font-medium text-sm">USB (WebUSB)</p>
              <p className="text-xs text-gray-500">{usbSupported ? 'Supported' : 'Not Supported'}</p>
            </div>
            {usbSupported && (
              <Button size="sm" variant="ghost" onClick={scanUSB} className="ml-auto">
                <Signal className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          <div className={`flex items-center gap-2 p-3 rounded-lg ${serialSupported ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <Cable className={`w-5 h-5 ${serialSupported ? 'text-green-600' : 'text-red-600'}`} />
            <div>
              <p className="font-medium text-sm">Serial (COM Ports)</p>
              <p className="text-xs text-gray-500">{serialSupported ? 'Supported' : 'Not Supported'}</p>
            </div>
            {serialSupported && (
              <Button size="sm" variant="ghost" onClick={scanSerial} className="ml-auto">
                <Signal className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          For best device support, use Chrome or Edge browser. Some features require HTTPS.
        </p>
      </Card>

      {/* Device Categories */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {Object.entries(DEVICE_TYPES).filter(([key]) => key !== 'UNKNOWN').map(([key, value]) => {
          const Icon = value.icon;
          const count = devices.filter(d => d.type === key).length;
          return (
            <Card key={key} className={`p-4 text-center border-2 ${count > 0 ? 'border-blue-200 bg-blue-50' : 'border-gray-200'}`}>
              <Icon className={`w-8 h-8 mx-auto mb-2 text-${value.color}-500`} />
              <p className="font-medium text-sm text-gray-800">{value.name}</p>
              <p className="text-2xl font-bold text-gray-700">{count}</p>
            </Card>
          );
        })}
      </div>

      {/* Connected Devices List */}
      <Card className="overflow-hidden">
        <div className="p-4 bg-gray-50 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <HardDrive className="w-5 h-5" />
            Connected Devices ({devices.length})
          </h3>
        </div>
        
        {devices.length === 0 ? (
          <div className="p-12 text-center">
            <Radio className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-800 mb-2">No Devices Connected</h4>
            <p className="text-gray-500 mb-4">Click "Scan for Devices" to detect connected hardware</p>
            <Button onClick={scanAllDevices} className="bg-blue-500 hover:bg-blue-600">
              <RefreshCw className="w-4 h-4 mr-2" />
              Start Scanning
            </Button>
          </div>
        ) : (
          <div className="divide-y">
            {devices.map(device => {
              const deviceType = DEVICE_TYPES[device.type] || DEVICE_TYPES.UNKNOWN;
              const Icon = deviceType.icon;
              
              return (
                <div 
                  key={device.id} 
                  className="p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {/* Device Icon */}
                    <div className={`w-12 h-12 rounded-xl bg-${deviceType.color}-100 flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-6 h-6 text-${deviceType.color}-600`} />
                    </div>
                    
                    {/* Device Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium text-gray-800">{device.name}</h4>
                        {getStatusBadge(device.status)}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          {getConnectionIcon(device.connection)}
                          {device.connection.charAt(0).toUpperCase() + device.connection.slice(1)}
                        </span>
                        <span>•</span>
                        <span>{deviceType.name}</span>
                        {device.brand && (
                          <>
                            <span>•</span>
                            <span>{device.brand}</span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => testDevice(device)}
                        className="flex items-center gap-1"
                      >
                        <TestTube className="w-4 h-4" />
                        Test
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => setSelectedDevice(device)}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => removeDevice(device.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Add Manual Device Modal */}
      {showAddManual && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAddManual(false)} />
          <Card className="relative z-10 w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-500" />
              Add Device Manually
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label>Device Name</Label>
                <Input 
                  placeholder="e.g., Honeywell 1900 Scanner"
                  value={manualDevice.name}
                  onChange={(e) => setManualDevice({...manualDevice, name: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Device Type</Label>
                <select 
                  className="w-full p-2 border rounded-lg"
                  value={manualDevice.type}
                  onChange={(e) => setManualDevice({...manualDevice, type: e.target.value})}
                >
                  {Object.entries(DEVICE_TYPES).filter(([key]) => key !== 'UNKNOWN').map(([key, value]) => (
                    <option key={key} value={key}>{value.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Connection Type</Label>
                <select 
                  className="w-full p-2 border rounded-lg"
                  value={manualDevice.connection}
                  onChange={(e) => setManualDevice({...manualDevice, connection: e.target.value})}
                >
                  <option value="usb">USB</option>
                  <option value="bluetooth">Bluetooth</option>
                  <option value="serial">Serial (COM Port)</option>
                  <option value="wifi">WiFi/Network</option>
                </select>
              </div>
              
              {(manualDevice.connection === 'serial' || manualDevice.connection === 'wifi') && (
                <div>
                  <Label>{manualDevice.connection === 'wifi' ? 'IP Address' : 'COM Port'}</Label>
                  <Input 
                    placeholder={manualDevice.connection === 'wifi' ? '192.168.1.100' : 'COM3'}
                    value={manualDevice.port}
                    onChange={(e) => setManualDevice({...manualDevice, port: e.target.value})}
                  />
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowAddManual(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={addManualDevice} className="flex-1 bg-blue-500 hover:bg-blue-600">
                Add Device
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Device Settings Modal */}
      {selectedDevice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedDevice(null)} />
          <Card className="relative z-10 w-full max-w-lg p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-500" />
              Device Settings: {selectedDevice.name}
            </h3>
            
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Device Type</p>
                    <p className="font-medium">{DEVICE_TYPES[selectedDevice.type]?.name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Connection</p>
                    <p className="font-medium capitalize">{selectedDevice.connection}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Brand</p>
                    <p className="font-medium">{selectedDevice.brand || 'Unknown'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Status</p>
                    <p className="font-medium capitalize">{selectedDevice.status}</p>
                  </div>
                </div>
              </div>
              
              {selectedDevice.type === 'BARCODE_SCANNER' && (
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-800">Scanner Settings</h4>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Auto-submit on scan</p>
                      <p className="text-xs text-gray-500">Automatically search when barcode is scanned</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Beep on scan</p>
                      <p className="text-xs text-gray-500">Play sound when barcode is detected</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                </div>
              )}
              
              {selectedDevice.type === 'RECEIPT_PRINTER' && (
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-800">Printer Settings</h4>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Auto-print receipts</p>
                      <p className="text-xs text-gray-500">Print receipt after each transaction</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Print order ticket</p>
                      <p className="text-xs text-gray-500">Print kitchen/picking ticket</p>
                    </div>
                    <Switch />
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setSelectedDevice(null)} className="flex-1">
                Close
              </Button>
              <Button 
                onClick={() => testDevice(selectedDevice)} 
                className="flex-1 bg-green-500 hover:bg-green-600"
              >
                <TestTube className="w-4 h-4 mr-2" />
                Test Connection
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Help Section */}
      <Card className="mt-6 p-4 bg-amber-50 border-amber-200">
        <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          Troubleshooting Tips
        </h4>
        <ul className="text-sm text-amber-700 space-y-1">
          <li>• <strong>USB devices</strong>: Make sure the device is plugged in before scanning</li>
          <li>• <strong>Bluetooth</strong>: Put the device in pairing mode before scanning</li>
          <li>• <strong>Serial ports</strong>: Check if drivers are installed for your device</li>
          <li>• <strong>Not detected?</strong> Try adding the device manually with the correct settings</li>
          <li>• <strong>Browser</strong>: Use Chrome or Edge for best hardware support</li>
        </ul>
      </Card>
    </div>
  );
};

export default DeviceSettingsPortal;
